<template>
  <div class="c-chat__userDetailsWrapper">
    <div class="c-chat__userDetails">
      <div class="t-alignCenter">
        <UserData :user="chatStore.userDetails" />
      </div>
      <div class="s-details__autoTranslate u-marginTop4 o-flex -gap2">
        <ToggleButton
          :value="autoTranslate"
          @change="onToggleAutoTranslate" />
        <div class="s-details__autoTranslateText t-alignLeft">
          <div class="t-body3 s-details__autoTranslateTextLabel">{{ $t('chat.auto_translate_toggle.label') }}</div>
          <div
            v-if="autoTranslate"
            class="t-caption1 u-colorDarkLightEmphasis u-marginTop1">
            {{ $t('chat.auto_translate_toggle.warning', { username: chatStore.userDetails?.displayName }) }}
          </div>
        </div>
      </div>

      <div
        ref="chatMediaTabsRef"
        class="c-chat__mediaTabs u-marginTop6">
        <div
          ref="chatTabActiveStateRef"
          class="c-chat__mediaTabActiveState"
          :style="activeTabIndicatorStyle" />
        <button
          v-for="(tab, index) in tabs"
          :key="tab.key"
          class="c-chat__mediaTabItem c-button -flat t-body3"
          :class="{ '-active': activeMediaTab === tab.key }"
          @click="changeMediaTab(tab.key, index, $event)">
          <SvgIcon
            small
            class="c-chat__mediaTabItemIcon"
            :type="tab.icon" />
          <span class="t-body3">{{ $t(tab.titleKey) }}</span>
        </button>
      </div>
    </div>

    <!-- Sliding Tab Content -->
    <div
      class="c-chat__mediaFilesWrapper">
      <div
        class="c-chat__mediaFiles"
        :style="{ transform: isRtl ? `translateX(${activeTabIndex * 100}%)` : `translateX(-${activeTabIndex * 100}%)`, '--max-attachments-height': mediaFilesMaxHeight + 'px' }">
        <div
          class="c-chat__mediaFilesItem">
          <UserImages
            v-if="activeMediaTab === 'IMAGES'"
            key="user-images" />
        </div>
        <div
          class="c-chat__mediaFilesItem">
          <UserFiles
            v-if="activeMediaTab === 'FILES'"
            key="user-files" />
        </div>
        <div
          class="c-chat__mediaFilesItem">
          <UserLinks
            v-if="activeMediaTab === 'LINKS'"
            key="user-links" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useTextDirection } from '@vueuse/core'
import {
  ref, computed, onMounted, nextTick,
} from 'vue'

import ToggleButton from '@/components/ToggleButton.vue'
import chatApi from '@/modules/chat/api/chat'
import { useChatStore } from '@/modules/chat/chatStore'
import UserData from '@/modules/chat/components/UserProfile/UserData.vue'
import UserFiles from '@/modules/chat/components/UserProfile/UserFiles.vue'
import UserImages from '@/modules/chat/components/UserProfile/UserImages.vue'
import UserLinks from '@/modules/chat/components/UserProfile/UserLinks.vue'

const chatStore = useChatStore()

const textDirection = useTextDirection()
const isRtl = computed(() => textDirection.value === 'rtl')

const tabs = ref([
  { key: 'IMAGES', titleKey: 'chat.media_tabs.images', icon: 'chat_images' },
  { key: 'FILES', titleKey: 'chat.media_tabs.files', icon: 'chat_file' },
  { key: 'LINKS', titleKey: 'chat.media_tabs.links', icon: 'chat_links' },
])

const activeMediaTab = ref('IMAGES')
const activeTabIndex = computed(() => tabs.value.findIndex((tab) => tab.key === activeMediaTab.value))

const activeTabPosition = ref(0)
const activeTabWidth = ref(0)
const mediaFilesMaxHeight = ref(0)
const chatTabActiveStateRef = ref(null)
const chatMediaTabsRef = ref(null)

const autoTranslate = computed(() => {
  return !!chatStore.chatThread?.auto_translate_enabled
})

const activeTabIndicatorStyle = computed(() => {
  const width = `${activeTabWidth.value}px`
  const offset = `${activeTabPosition.value}px`

  if (isRtl.value) {
    return {
      width,
      right: offset,
      left: 'auto',
    }
  }

  return {
    width,
    left: offset,
    right: 'auto',
  }
})

const onToggleAutoTranslate = async ({ value }) => {
  if (!chatStore.chatThread) {
    return
  }
  try {
    await chatApi.setAutoTranslate(chatStore.chatThread.id, !!value)
    chatStore.setThreadAutoTranslate(chatStore.chatThread.azure_chat_thread_id, !!value)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
  }
}

const updateTabIndicator = (element) => {
  let offsetToDeduct = 0
  if (chatMediaTabsRef.value.lastChild === element) {
    offsetToDeduct = 4 // for the gap between elements, and to not make the active state overlap with tabs wrapper
  }

  const elementWidth = element.offsetWidth
  const parentElement = chatMediaTabsRef.value

  activeTabWidth.value = elementWidth - offsetToDeduct

  if (isRtl.value && parentElement) {
    // In RTL layouts we need to position the active tab indicator from the right edge of the
    // container (not from the left), otherwise the pill would not sit directly under the tab.
    const parentWidth = parentElement.offsetWidth
    const offsetFromLeft = element.offsetLeft
    activeTabPosition.value = parentWidth - offsetFromLeft - elementWidth - offsetToDeduct
  } else {
    activeTabPosition.value = element.offsetLeft - offsetToDeduct
  }
}

const changeMediaTab = async (key, index, event) => {
  activeMediaTab.value = key
  await nextTick()
  updateTabIndicator(event.target)
}

onMounted(() => {
  const activeTab = document.querySelector('.c-chat__mediaTabItem.-active')
  if (activeTab) {
    updateTabIndicator(activeTab)
  }
  setTimeout(() => {
    mediaFilesMaxHeight.value = document.querySelector('.c-chat__userDetailsWrapper').getBoundingClientRect().height - document.querySelector('.c-chat__userDetails').getBoundingClientRect().height
  }, 100)
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/config';

.s-details__autoTranslate {
  align-self: flex-start;
  margin-inline-start: $spacing-4;
}
.s-details__autoTranslateText {
  max-width: 500px;
}
.s-details__autoTranslateTextLabel {
  /* Has to be a "magic" number to fit the sizing of the toggle */
  margin-top: 6px;
}
</style>
